// src/pages/Admin/EditOnboardingApplication.js

import * as Yup from "yup";
import {
    Box,
    Container,
    Grid,
    TextField,
    Typography,
    Autocomplete,
    InputAdornment,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    Button,
} from "@mui/material";
import { Formik } from "formik"; // Retain Formik for the overall flow
import { useEffect, useState, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import Select from "react-select";
import { Log, getCountryList, getEntityFilterConditions, sendDataWithPost, checkUENExists, getActiveBusinessEntity, submitOnboardingStep, getTerminalOptions, getCurrentUserEntity } from "../../services/services";
import { showConfirmDialog } from "../../utils/toast";
import { trimValues } from "../../utils/appUtils";

import api from "../../services/api";
import { urlRegex, INTERNATIONAL_COUNTRY_LIST, MAPPED_REGISTERED_COUNTRY, RISK_LEVEL, MPI_TYPE, SUB_MERCHANT_COUNTRY_CODE } from "../../utils/constants";
import { parsePhoneNumberFromString, getCountries, getCountryCallingCode } from "libphonenumber-js";
import metadata from 'libphonenumber-js/metadata.full.json'
import '../../pages/Admin/PhoneInput.css';
import { Close, GetApp } from "@mui/icons-material";
import toast from "react-hot-toast";

// Import the step-specific components
import Step1BusinessEntity from "./Step1BusinessEntity";
import Step2Merchant from "./Step2Merchant";
import Step3Terminal from "./Step3Terminal";
import Step4KYC from "./Step4KYC";
import Step5Final from "./Step5Final";
import { set } from "lodash";
import EditStep1BusinessEntity from "./EditStep1BusinessEntity";
import EditStep2Merchant from "./EditStep2Merchant";
import EditStep3Terminal from "./EditStep3Terminal";
import EditStep4Fees from "./EditStep4Fees";
import { downloadMafV370Pdf } from "../../utils/mafPdfV370Export";


// --- Utility Constants (Retained) ---
const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const PRIORITY_CODES = ["SG", "MY", "TH"];
const allCountries = getCountries(metadata)
    .map((code) => ({
        code,
        label: regionNames.of(code),
        dialCode: "+" + getCountryCallingCode(code, metadata),
    }))
    .filter((country) => country.dialCode !== "+247");

const priorityCountries = PRIORITY_CODES.map((pCode) =>
    allCountries.find((c) => c.code === pCode)
).filter(Boolean);
const otherCountries = allCountries.filter(
    (c) => !PRIORITY_CODES.includes(c.code)
);
export const COUNTRIES = [...priorityCountries, ...otherCountries];

const APPLICATION_TYPES = [
    { value: 'ONLINE', label: 'ONLINE' },
    { value: 'OFFLINE', label: 'OFFLINE' },
    { value: 'ONLINE_AND_OFFLINE', label: 'ONLINE_AND_OFFLINE' }
];

// Onboarding steps
const steps = [
    'Business Entity',
    'Merchant & Bank Details',
    'Terminal Details',
    'Settlement & Fees',
    'KYC Review',
    'Final Approval'
];
// --- End Utility Constants ---
export const DRAFT_KEY = 'onboarding_draft';

const EditOnboardingApplication = ({ userRole }) => {
    const { id } = useParams();
    const history = useHistory();
    // Global State for Stepper
    const [activeStep, setActiveStep] = useState(0);
    const [onboardingAppId, setOnboardingAppId] = useState(null); // Master Onboarding ID from Step 1
    const [numMerchants, setNumMerchants] = useState(1); // Number of merchants from Step 1
    const [allFormData, setAllFormData] = useState({}); // Stores data for all steps
    const [isLoading, setIsLoading] = useState(false); // Global loading state
    const [isDataLoaded, setIsDataLoaded] = useState(false); // Flag for initial data load completion
    const [mode, setMode] = useState(null); // Mode from API response (e.g., 'REVIEW')
    const [appicationStatus, setAppicationStatus] = useState(null); 

    // Determine if a step is read-only based on userRole and application status
    const isStepReadOnly = (step) => {
        if (appicationStatus === 'FINAL_APPROVED') return true;

        const hasEdit = userRole && userRole.includes('Edit');
        const hasFinalApproval = userRole && userRole.includes('FinalApproval');
        const hasComplianceCheck = userRole && userRole.includes('ComplianceCheck');

        if (!hasEdit) {
            // Final Approver only: restrict to step 5
            if (hasFinalApproval) return step !== 5;
            // Compliance Checker only: restrict to step 4
            if (hasComplianceCheck) return step !== 4;
            // Requestor: locked once submitted
            if (appicationStatus === 'PENDING_COMPLIANCE_CHECK' || appicationStatus === 'PENDING_FINAL_APPROVAL') {
                return true;
            }
            return step > 3;
        }

        // Full Edit rights: steps 4 and 5 gated by specific permissions
        if (step === 4) return !hasComplianceCheck;
        if (step === 5) return !hasFinalApproval;
        return false;
    };


    const loadDraft = () => {
        try {
            const raw = window.localStorage.getItem(DRAFT_KEY);
            if (!raw) return null;
            let localData = JSON.parse(raw);

            // --- NEW LOGIC: FLATTEN STEP 2 MERCHANT DATA ---
            if (localData.allFormData && localData.allFormData["1"] && Array.isArray(localData.allFormData["1"].items)) {
                // Check if the merchant data is nested (API response structure)
                const firstItem = localData.allFormData["1"].items[0];
                if (firstItem && firstItem.merchant) {
                    localData.allFormData["1"].items = localData.allFormData["1"].items.map(item => {
                        // Merge fields from 'item' (like 'id') with fields from 'merchant'
                        return {
                            ...item.merchant,
                            id: item.id, // Retain the merchant's ID
                        };
                    });
                    // console.log("Flattened Step 2 Merchant data from draft.");
                }
            }
            // --- END NEW LOGIC ---

            return localData;
        } catch (e) {
            Log.error('Failed to parse onboarding draft from storage:', e);
            return null;
        }
    };
    const saveDraft = (partial) => {
        try {
            const current = loadDraft() || {};
            const next = {
                version: 1,
                updatedAt: new Date().toISOString(),
                activeStep,
                onboardingAppId,
                allFormData,
                ...current,
                ...partial,
            };
            window.localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
        } catch (e) {
            Log.error('Failed to save onboarding draft to storage:', e);
        }
    };
    const clearDraft = () => {
        try {
            window.localStorage.removeItem(DRAFT_KEY);
        } catch (e) {
            Log.error('Failed to clear onboarding draft from storage:', e);
        }
    };

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error',
    });

    const handleSnackbarClose = (event, reason) => {
        // Avoid closing on external click (if reason === 'clickaway')
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // Original Data fetching states (Retained for Step 1)
    const [countryList, setCountryList] = useState([]);
    const [entityFilterConditions, setEntityFilterConditions] = useState([]);
    const [_, setEntityFilterConditionsLoaded] = useState(false);
    // const [bankAccounts, setBankAccounts] = useState([]);
    // const [bankAccountsLoaded, setBankAccountsLoaded] = useState(false);
    const [fetchedApplicationData, setFetchedApplicationData] = useState(null);
    const freshFormDataRef = useRef({});
    const [activeBusinessEntity, setActiveBusinessEntity] = useState([]);
    const [activeBusinessEntityLoaded, setActiveBusinessEntityLoaded] = useState(false);
    const [mafExportLoading, setMafExportLoading] = useState(false);
    const [terminalOptions, setTerminalOptions] = useState([]);
    const [terminalOptionsLoaded, setTerminalOptionsLoaded] = useState(false);
    const [userOxygen7Entity, setUserOxygen7Entity] = useState(null);
    const stepIndex = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4 };

    const fetchApplicationDataFunc = async (id) => {
        try {
            setIsLoading(true);
            const existingDraft = loadDraft();
            // Call API to get application details by ID
            const response = await api.get(`/merchant/onboarding/${id}/get-onboarding-application/`);
            const appData = response.data;

            // console.log('API response data:', appData);

            setFetchedApplicationData(appData);
            setMode(appData.mode); // Extract mode from API response
            setAppicationStatus(appData.application.status);

            // Prefer locally-saved KYC draft (correct Formik field names) over API response
            const apiKyc = appData.kyc;
            const draftKyc = String(existingDraft?.onboardingAppId) === String(id)
                ? existingDraft?.allFormData?.[4]
                : null;
            const draftKycHasData = draftKyc && (
                draftKyc.merchant_category_code != null ||
                draftKyc.last_kyc_review != null ||
                draftKyc.risk_level != null
            );
            const kycData = draftKycHasData ? draftKyc : (apiKyc || {});

            const allStepDataTransform = {
                0: {
                    ...(appData.business_entity?.business_entity || {}),
                    onboarding_application_id: id,
                    business_entity_id: appData.business_entity?.id,
                    number_of_merchants: appData.merchants?.length || appData.business_entity?.number_of_merchants || 0
                },
                1: { items: (appData.merchants || []).map(m => ({ ...m.merchant, id: m.id })) },
                2: (() => {
                    const rawTerminals = appData.terminals || [];
                    // New format: terminal.terminal_mode exists → stored as { merchants: [...] }
                    if (rawTerminals.length > 0 && rawTerminals[0]?.terminal?.terminal_mode !== undefined) {
                        return { merchants: rawTerminals.map(t => ({ ...t.terminal })) };
                    }
                    // Old format
                    return { terminals: rawTerminals.map(t => ({ ...t.terminal, id: t.id })) };
                })(),
                3: appData.fees || {},
                4: kycData,
                5: appData.application || {},
            };

            freshFormDataRef.current = allStepDataTransform;
            setAllFormData(allStepDataTransform);
            setOnboardingAppId(id);
            setNumMerchants(appData.merchants?.length || appData.business_entity?.number_of_merchants || 1);
            saveDraft({ allFormData: allStepDataTransform, onboardingAppId: id });
            // Determine which step to show next based on available data
            if (appData.terminals?.length > 0) {
                setActiveStep(3); // Fees step (next after terminals)
            } else if (appData.merchants?.length > 0) {
                setActiveStep(2); // Terminal step
            } else {
                setActiveStep(1); // Merchant step
            }

            // Override activeStep based on application status + userRole
            const appStatus = appData.application.status;
            const hasFinalApproval = userRole && userRole.includes('FinalApproval');
            const hasComplianceCheck = userRole && userRole.includes('ComplianceCheck');
            if (appStatus === 'PENDING_COMPLIANCE_CHECK' && hasComplianceCheck) {
                setActiveStep(4);
            } else if (appStatus === 'PENDING_FINAL_APPROVAL' && hasFinalApproval) {
                setActiveStep(5);
            } else if (hasFinalApproval) {
                setActiveStep(5);
            } else if (hasComplianceCheck) {
                setActiveStep(4);
            }

        } catch (error) {
            Log.error('Error fetching application data:', error);
            // Handle errors (e.g., show Snackbar)
        } finally {
            setIsLoading(false);
            setIsDataLoaded(true);
        }
    };


    useEffect(() => {
        getCountryList(setCountryList);
        getEntityFilterConditions(setEntityFilterConditions, setEntityFilterConditionsLoaded);
        // getBankAccounts();
        getActiveBusinessEntity(setActiveBusinessEntity, setActiveBusinessEntityLoaded);
        getTerminalOptions(setTerminalOptions, setTerminalOptionsLoaded);
        getCurrentUserEntity().then(entity => { if (entity) setUserOxygen7Entity(entity); });
        if (id) {
            fetchApplicationDataFunc(id);
        } else {
            setIsDataLoaded(true);
            // Set default activeStep based on userRole when not editing
            if (userRole && userRole.includes('FinalApproval')) {
                setActiveStep(5);
            } else if (userRole && userRole.includes('ComplianceCheck')) {
                setActiveStep(4);
            }
        }
        // Hydrate from local draft to avoid unnecessary API calls
        const draft = loadDraft();
        if (draft) {
            if (typeof draft.activeStep === 'number') setActiveStep(draft.activeStep);
            if (draft.allFormData) setAllFormData(draft.allFormData);
            if (draft.onboardingAppId) setOnboardingAppId(draft.onboardingAppId);
            if (draft.numMerchants) setNumMerchants(draft.numMerchants);
        }
    }, []);

    // Keep draft storage in sync when onboardingAppId changes
    useEffect(() => {
        saveDraft({ onboardingAppId });
    }, [onboardingAppId]);

    // --- Stepper Navigation ---
    const handleNext = () => {
        setActiveStep((prevActiveStep) => {
            const next = prevActiveStep + 1;
            saveDraft({ activeStep: next });
            return next;
        });
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => {
            const next = prevActiveStep - 1;
            saveDraft({ activeStep: next });
            return next;
        });
    };

    /**
     * Main function to handle form submission for the current step,
     * calls the corresponding API, and advances the step.
     * @param {object} values - Formik values from the current step's form.
     * @param {object} actions - Formik actions (setSubmitting).
     */
    const handleComplianceCheck = async (action, values) => {
        setIsLoading(true);
        try {
            const url = `/merchant/onboarding/${onboardingAppId}/save-step-4/`;
            const data = { action, kyc_data: values };
            const response = await submitOnboardingStep(url, data);
            if (response?.status === 200 || response?.status === 201) {
                const updatedAllFormData = { ...allFormData, 4: values };
                setAllFormData(updatedAllFormData);
                saveDraft({ allFormData: updatedAllFormData });
                handleNext();
            }
        } catch (error) {
            Log.error('Error submitting approve/reject decision:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFinalApprove = async (action, values) => {
        setIsLoading(true);
        try {
            const url = `/merchant/onboarding/${onboardingAppId}/save-step-5/`;
            const data = {
                action: action,
                data: values
            };
            // await api.post(url, data);
            const response = await submitOnboardingStep(url, data);
            // On success, move to next step
            if (response?.status === 200 || response?.status === 201) {
                handleNext();
            }
        } catch (error) {
            Log.error('Error submitting approve/reject decision:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStepSubmit = async (values, actions) => {
        setIsLoading(true);
        // Store current step data locally and persist draft
        setAllFormData((prev) => {
            const updated = { ...prev, [activeStep]: values };
            saveDraft({ allFormData: updated });
            return updated;
        });

        try {
            let response;            

            // Get businessEntityId from allFormData or localStorage draft

            // --- Step 1: Business Entity (Update) ---
            if (activeStep === 0) {
                const currentNumMerchants = allFormData[0]?.number_of_merchants;
                if (values.number_of_merchants !== currentNumMerchants) {
                    const hasData = (allFormData[1]?.items?.length > 0) || (allFormData[2]?.terminals?.length > 0);
                    if (hasData) {
                        const confirmed = await showConfirmDialog('If you change the number of merchants, the data of step 2 and step 3 will be deleted.');
                        if (!confirmed) {
                            actions.setSubmitting(false);
                            setIsLoading(false);
                            return;
                        }
                        // Clear step 2 and 3
                        setAllFormData(prev => {
                            const updated = { ...prev };
                            delete updated[1];
                            delete updated[2];
                            return updated;
                        });
                        saveDraft({ allFormData: { ...allFormData, 1: undefined, 2: undefined } });
                    }
                }

                const businessEntityId = allFormData[0]?.business_entity_id || loadDraft()?.allFormData?.[0]?.business_entity_id;
                Log.info(`Submitting Step 1: Business Entity`, values);
                const merchant_id_with_source = values.merchant_payout?.map(item => `${item?.id}_${item?.source}`);

                // Prepare data matching the backend's expected structure for save-step-1/
                const businessEntityData = trimValues({
                    ...values,
                    // last_kyc_review: values.last_kyc_review ? new Date(values.last_kyc_review).toISOString() : null,
                    // next_kyc_review: values.next_kyc_review ? new Date(values.next_kyc_review).toISOString() : null,
                    // Map Autocomplete/Select values back to IDs/strings for the backend
                    registered_country: values.registered_country, // Already an ID
                    registered_country_name: (() => {
                        const selectedOption = countryList.find(option => option.id === values.registered_country);
                        return selectedOption ? MAPPED_REGISTERED_COUNTRY[selectedOption.registered_country] : "";
                    })(),
                    address_country: values.address_country,
                    // application_type: values.application_type.id,
                    // Multi-selects map to list of IDs/objects
                    // merchant_payout: values.merchant_payout.map(item => item.id),
                    merchant_id_with_source: merchant_id_with_source,
                    // bank_accounts: values.bank_accounts.map(item => item.id),
                    action: "update",
                    pk: businessEntityId,
                });

                const dataToSend = {
                    business_entity_data: businessEntityData,
                    number_of_merchants: values.number_of_merchants,
                    application_type: values.application_type.id,
                };
                const url = `/merchant/onboarding/save-step-1/`;
                response = await submitOnboardingStep(url, dataToSend);

                // Update number of Merchants for subsequent steps
                setNumMerchants(values.number_of_merchants);
                const registeredCountryName = (() => {
                    const selectedOption = countryList.find(option => option.id === values.registered_country);
                    return selectedOption ? MAPPED_REGISTERED_COUNTRY[selectedOption.registered_country] : "";
                })();

                // Update form data with number_of_merchants for future edits
                setAllFormData((prev) => ({
                    ...prev,
                    0: {
                        ...prev[0],
                        number_of_merchants: values.number_of_merchants,
                        registered_country_name: registeredCountryName,
                    },
                }));
            }
            // --- Step 2: Merchant Details (Update) ---
            else if (activeStep === 1) {
                if (!onboardingAppId) throw new Error("Onboarding Application ID is missing.");
                // return false;

                // Add merchant_category_name to each merchant
                const merchantsWithNames = values.items.map(merchant => {
                  const mcc = activeBusinessEntity?.mcc?.find(m => m.id === merchant.merchant_category_code);
                  return {
                    ...merchant,
                    merchant_category_name: mcc ? mcc.code_description : '',
                  };
                });

                // Step 2 API expects a list of merchant objects (values.items is the list)
                const url = `/merchant/onboarding/${onboardingAppId}/save-step-2/`;
                response = await submitOnboardingStep(url, merchantsWithNames);

                // Update allFormData with the response merchants
                const updatedAllFormData = { ...allFormData, 1: { items: response.data.merchants } };

                // Propagate updated merchant ids/names into step 3 if already filled
                if (updatedAllFormData[2]?.merchants) {
                    updatedAllFormData[2] = {
                        ...updatedAllFormData[2],
                        merchants: updatedAllFormData[2].merchants.map((m, i) => ({
                            ...m,
                            merchant_id: updatedAllFormData[1].items[i]?.id || m.merchant_id,
                            merchant_name: updatedAllFormData[1].items[i]?.name || m.merchant_name,
                        })),
                    };
                }

                setAllFormData(updatedAllFormData);

                // Persist draft for step2 like step1
                saveDraft({ allFormData: updatedAllFormData });
            }
            // --- Step 3: Terminal Details ---
            else if (activeStep === 2) {
                if (!onboardingAppId) throw new Error("Onboarding Application ID is missing.");
                Log.info(`Submitting Step 3: Terminal Details`, values);

                const url = `/merchant/onboarding/${onboardingAppId}/save-step-3/`;
                response = await submitOnboardingStep(url, values);

                // Sync merchant_id / merchant_name from step 2 back into step 3 merchants
                const step3Data = response?.data || values;
                if (step3Data.merchants && allFormData[1]?.items) {
                    step3Data.merchants = step3Data.merchants.map((m, i) => ({
                        ...m,
                        merchant_id: allFormData[1].items[i]?.id || m.merchant_id,
                        merchant_name: allFormData[1].items[i]?.name || m.merchant_name,
                    }));
                }

                const updatedAllFormData = { ...allFormData, 2: step3Data };
                setAllFormData(updatedAllFormData);
                saveDraft({ allFormData: updatedAllFormData });
            }
            // --- Step 4: Settlement & Fees ---
            else if (activeStep === 3) {
                if (!onboardingAppId) throw new Error("Onboarding Application ID is missing.");
                Log.info(`Submitting Step 4: Settlement & Fees`, values);

                const url = `/merchant/onboarding/${onboardingAppId}/save-step-fees/`;
                response = await submitOnboardingStep(url, values);

                const feesData = response?.data || values;
                freshFormDataRef.current = { ...freshFormDataRef.current, 3: feesData };
                const updatedAllFormData = { ...allFormData, 3: feesData };
                setAllFormData(updatedAllFormData);
                saveDraft({ allFormData: updatedAllFormData });
            }
            // --- Add logic for remaining steps here ---
            // ...
            else {
                // Default action for remaining steps (if API logic isn't defined yet)
                // For Step 4 (Final Submission), use save-step-5-final-decision/
                Log.info(`Skipping API call for Step ${activeStep + 1}`);
            }

            // Unblock Formik before advancing — handleNext() unmounts the current step component
            // so any Formik state update after that triggers the "unmounted component" warning
            actions.setSubmitting(false);
            setIsLoading(false);

            // Move to the next step
            handleNext();

        } catch (error) {
            // console.log({ 'error': error });
            Log.error('Submission error:', error);

            let errorMessage = "An unknown error occurred during submission.";

            if (error.response && error.response.data) {
                const errorData = error.response.data;

                //1. Prioritize general errors (detail, non_field_errors)
                if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.non_field_errors) {
                    //Errors are not tied to a specific field (e.g. 'You already have a pending application')
                    errorMessage = errorData.non_field_errors.join('; ');
                }
                //2. Handle validation errors of a specific field (for example, UEN errors)
                else {
                    const firstKey = Object.keys(errorData)[0];
                    if (firstKey) {
                        const errorValue = errorData[firstKey];
                        const firstError = Array.isArray(errorValue) ? errorValue[0] : errorValue;

                        errorMessage = `${firstKey.replace(/_/g, ' ').toUpperCase()}: ${firstError}`;
                    }
                }
            } else {
                errorMessage = "Network error or server unreachable. Please check your connection.";
            }

            // --- SHOW ERROR VIA SNACKBAR ---
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });

            // Restore state
            actions.setSubmitting(false);
            setIsLoading(false);
            // Log.error(`Error saving Step ${activeStep + 1}:`, error.response?.data || error.message);
            // alert(`Error saving data. Please check the console for details. Error: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };

    // readOnly is handled by each step component via its own disabled props
    const wrapReadOnly = (content) => content;

    const getStepContent = (step) => {
        // Shared props derived from original component's state
        const sharedProps = { countryList, COUNTRIES, APPLICATION_TYPES };
        const isReadOnly = isStepReadOnly(step);
        switch (step) {
            case 0:
                return wrapReadOnly(
                    <EditStep1BusinessEntity
                        key={activeStep}
                        formData={allFormData[0]}
                        onNext={handleStepSubmit}
                        onSkip={handleNext}
                        isSubmitting={isLoading}
                        {...sharedProps}
                        entityFilterConditions={entityFilterConditions}
                        activeBusinessEntity={activeBusinessEntity}
                        readOnly={isReadOnly}
                    />,
                    isReadOnly
                );
            case 1:
                if (!onboardingAppId) return <Typography>Please complete the Business Entity step first.</Typography>;
                return wrapReadOnly(
                    <EditStep2Merchant
                        key={activeStep}
                        onboardingAppId={onboardingAppId}
                        numMerchants={numMerchants}
                        onNext={handleStepSubmit}
                        onSkip={handleNext}
                        onBack={handleBack}
                        isSubmitting={isLoading}
                        business_entity_id={allFormData[0]?.business_entity_id}
                        {...sharedProps}
                        activeBusinessEntity={activeBusinessEntity}
                        userOxygen7Entity={userOxygen7Entity}
                        readOnly={isReadOnly}
                    />,
                    isReadOnly
                );
            case 2:
                return wrapReadOnly(
                    <EditStep3Terminal
                        onNext={handleStepSubmit}
                        onSkip={handleNext}
                        onBack={handleBack}
                        isSubmitting={isLoading}
                        onboardingAppId={onboardingAppId}
                        business_entity_id={allFormData[0]?.business_entity_id}
                        readOnly={isReadOnly}
                        onGenerateMaf={handleDownloadPrefilledMaf}
                        mafLoading={mafExportLoading}
                        merchantData={allFormData[1]}
                        formData={allFormData[2]}
                    />,
                    isReadOnly
                );
            case 3:
                return wrapReadOnly(
                    <EditStep4Fees
                        formData={allFormData[3]}
                        onNext={handleStepSubmit}
                        onSkip={handleNext}
                        onBack={handleBack}
                        isSubmitting={isLoading}
                        readOnly={isReadOnly}
                    />,
                    isReadOnly
                );
            case 4:
                return wrapReadOnly(
                    <Step4KYC
                        formData={allFormData[4]}
                        onNext={handleComplianceCheck}
                        onSkip={handleNext}
                        onBack={handleBack}
                        isSubmitting={isLoading}
                        onboardingAppId={onboardingAppId}
                        business_entity_id={allFormData[0]?.business_entity_id}
                        activeBusinessEntity={activeBusinessEntity}
                        businessEntityName={allFormData[0]?.name}
                        readOnly={isReadOnly}
                        hideCheckbox={isReadOnly || (userRole && userRole.includes('FinalApproval') && !userRole.includes('ComplianceCheck'))}
                    />,
                    isReadOnly
                );
            case 5:
                return (
                    <Step5Final
                        onNext={handleFinalApprove}
                        onBack={handleBack}
                        isSubmitting={isLoading}
                        onboardingAppId={onboardingAppId}
                        business_entity_id={allFormData[0]?.business_entity_id}
                        businessEntityName={allFormData[0]?.name}
                        readOnly={isReadOnly}
                        hideCheckbox={isReadOnly || (userRole && userRole.includes('FinalApproval'))}
                    />
                );
            default:
                return "Unknown step";
        }
    };

    // helper to control clickable steps
    const isStepEnabled = (index) => {
        if (index === 0) return true;
        const hasOnboardingId = Boolean(onboardingAppId) || Boolean(id);
        if (!hasOnboardingId) return false;
        if (index === 1) return true;

        // Check data conditions only if fetchedApplicationData exists (edit mode)
        if (fetchedApplicationData) {
            const hasTerminals = fetchedApplicationData.terminals && fetchedApplicationData.terminals.length > 0;
            const hasMerchants = fetchedApplicationData.merchants && fetchedApplicationData.merchants.length > 0;
            if (index === 2 && !hasMerchants) return false;
            if ((index === 3 || index === 4) && (!hasTerminals || !hasMerchants)) return false;
            if (index === 5 && (!hasTerminals || !hasMerchants)) return false;
        }

        if (index === 2) return true;
        if (index === 3) return true;
        if (index === 4) return true;
        if (index === 5) return (userRole && userRole.includes('FinalApproval')) || appicationStatus === 'FINAL_APPROVED';
        return false;
    };

    const handleDownloadPrefilledMaf = async (currentTerminalValues) => {
        const abeRaw = activeBusinessEntity;
        const abe = Array.isArray(abeRaw) ? abeRaw[0] : abeRaw;
        const data = freshFormDataRef.current;
        const terminalsStep = currentTerminalValues || data[2] || allFormData[2];
        try {
            setMafExportLoading(true);
            await downloadMafV370Pdf(
                {
                    businessEntity: data[0] || allFormData[0],
                    merchantsStep: data[1] || allFormData[1],
                    terminalsStep,
                    feesStep: allFormData[3] || data[3],
                    countryList,
                    activeBusinessEntity: abe,
                },
                `MAF_prefilled_${onboardingAppId || id || "draft"}.pdf`
            );
            toast.success("MAF PDF downloaded.");
        } catch (e) {
            toast.error(e?.message || "Failed to generate MAF PDF.");
        } finally {
            setMafExportLoading(false);
        }
    };

    const canDownloadMaf = Boolean((onboardingAppId || id) && isDataLoaded);

    return (
        <Container maxWidth="xl">
            <Box sx={{ width: '100%', bgcolor: 'background.paper', p: 3, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h4">
                        Edit Onboarding Application
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {activeStep >= 3 && (
                            <Button
                                type="button"
                                variant="outlined"
                                startIcon={<GetApp />}
                                disabled={!canDownloadMaf || mafExportLoading}
                                onClick={() => handleDownloadPrefilledMaf()}
                            >
                                {mafExportLoading ? "Generating…" : "Download prefilled MAF (PDF)"}
                            </Button>
                        )}
                        <Button
                            type="button"
                            onClick={() => history.push('/3/app/operations/onboarding')}
                            variant="outlined"
                        >
                            Close
                        </Button>
                    </Box>
                </Box>

                {/* Custom Stepper UI (Retained from original file) */}
                <Box sx={{
                    width: '100%',
                    py: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 3
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mx: 4
                    }}>
                        {steps.map((label, index) => {
                            const enabled = isStepEnabled(index);
                            return (
                                <Box
                                    key={label}
                                    onClick={() => { if (enabled) { setActiveStep(index); saveDraft({ activeStep: index }); } }}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        position: 'relative',
                                        flex: 1,
                                        cursor: enabled ? 'pointer' : 'default',
                                        pointerEvents: enabled ? 'auto' : 'auto',
                                        opacity: enabled ? 1 : 0.7,
                                    }}
                                >
                                    <Box sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        bgcolor: index === activeStep ? 'primary.main' :
                                            index < activeStep ? 'success.main' : 'grey.300',
                                        color: index <= activeStep ? 'common.white' : 'text.secondary',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold',
                                        mb: 1,
                                        zIndex: 1
                                    }}>
                                        {index + 1}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: index <= activeStep ? 'text.primary' : 'text.secondary',
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                    {index < steps.length - 1 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                width: '100%',
                                                left: '50%',
                                                top: 16,
                                                borderTop: 2,
                                                borderColor: index < activeStep ? 'success.main' : 'grey.300',
                                                zIndex: 0
                                            }}
                                        />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* Form Content Area */}
                <Box sx={{ p: 2 }}>
                    {isLoading && !isDataLoaded ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                            <Typography>Loading...</Typography>
                        </Box>
                    ) : activeStep === steps.length ? (
                        (() => {
                            // Clear the persisted draft once onboarding is completed
                            clearDraft();
                            return (
                                <Box>
                                    <Grid container spacing={4} sx={{ py: 2, px: 2 }}>
                                        <Typography align="center" textAlign={'center'} sx={{ mt: 2, mb: 1 }}>All steps completed - Application submitted.</Typography>
                                    </Grid>
                                </Box>
                            );
                        })()
                    ) : (
                        <>
                            {getStepContent(activeStep)}
                        </>
                    )}
                </Box>
            </Box>
        </Container>
    );
};

export default EditOnboardingApplication;